'use client';

/**
 * GlobalNetwork — 글로벌 크로스보더 커버리지 세계지도.
 *
 * Hub-and-spoke network over a world map: the Seoul hub connects to each
 * country cluster with an animated arc. Hovering / focusing a region chip
 * (desktop, ≥sm) opens a popover listing the advisors covering it; clicking
 * pins it, Escape or pointer-down outside closes it. A visually-hidden list
 * mirrors the popover content for screen readers. Below `sm` the map is
 * followed by region chips + a detail panel.
 *
 * react-simple-maps renders the locally bundled Natural Earth TopoJSON as SVG.
 * All motion is gated behind prefers-reduced-motion; the travelling dots
 * additionally behind @supports offset-path (they sit at the viewBox origin
 * otherwise).
 */

import { useEffect, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import type { GlobalRegion } from '@/components/marketing/data/team';
import CountryFlag from '@/components/marketing/primitives/CountryFlag';
import worldMapData from '@/components/marketing/data/world-110m.json';

/**
 * User-facing strings. The defaults are Korean, so the /company call site needs
 * no change; the English firm page passes its own set. Plain strings rather than
 * formatter functions — functions cannot cross the Server → Client boundary.
 */
export interface GlobalNetworkCopy {
  /** Label on the Seoul hub node. */
  hub: string;
  /** Region chip aria-label — `{region}` is replaced with the region name. */
  regionAria: string;
  /** Hint shown under the map. */
  hint: string;
  /** Heading of the screen-reader-only region list. */
  srHeading: string;
}

const KO_COPY: GlobalNetworkCopy = {
  hub: '국제법무 Control Tower',
  regionAria: '{region} 보기',
  hint: '각 거점에 마우스를 올리거나 선택하면 지원 가능한 구성원이 표시됩니다.',
  srHeading: '거점별 지원 구성원',
};

const VIEW_W = 960;
const VIEW_H = 430;
const HUB = { x: 677, y: 140 };
const HIDE_DELAY_MS = 160;

interface NodeLayout {
  x: number;
  y: number;
  /** Popover placement relative to the node. */
  side: 'left' | 'right' | 'top';
  /** Quadratic bezier control point for the hub arc. */
  cx: number;
  cy: number;
  /**
   * Chip anchor when the default spot just above the node would collide with
   * a neighbouring chip or the hub label. Omitted means (x, y - 30).
   */
  chip?: { x: number; y: number };
}

/** Node positions in SVG user units — aligned to each region's approximate map position. */
const NODE_LAYOUT: Record<string, NodeLayout> = {
  us: { x: 180, y: 138, side: 'right', cx: 410, cy: 72 },
  cn: { x: 629, y: 147, side: 'right', cx: 648, cy: 86 },
  // Japan sits beside the Seoul hub and China: its chip moves up and right, and
  // its popover opens left so it stays inside the map box.
  jp: { x: 703, y: 144, side: 'left', cx: 710, cy: 82, chip: { x: 772, y: 56 } },
  vn: { x: 649, y: 199, side: 'right', cx: 671, cy: 184 },
  world: { x: 456, y: 338, side: 'top', cx: 560, cy: 272 },
};

/** ISO 3166-1 numeric ids in the local Natural Earth TopoJSON. */
const COUNTRY_REGION_BY_NUMERIC_ID: Record<string, string> = {
  '840': 'us',
  '156': 'cn',
  '704': 'vn',
  '392': 'jp',
};

/** Natural Earth projection settings for a wide, Korea-first coverage map. */
const MAP_PROJECTION = 'geoNaturalEarth1';
const MAP_PROJECTION_CONFIG = { scale: 155, center: [35, 10] };

const pct = (value: number, total: number) => `${(value / total) * 100}%`;

const chipPosition = (layout: NodeLayout) => {
  const anchor = layout.chip ?? { x: layout.x, y: layout.y - 30 };
  return { left: pct(anchor.x, VIEW_W), top: pct(anchor.y, VIEW_H) };
};

const arcPath = (layout: NodeLayout) => `M ${HUB.x} ${HUB.y} Q ${layout.cx} ${layout.cy} ${layout.x} ${layout.y}`;

const POPOVER_TRANSFORM: Record<NodeLayout['side'], string> = {
  right: 'translate(70px, -50%)',
  left: 'translate(calc(-100% - 70px), -50%)',
  top: 'translate(-50%, calc(-100% - 30px))',
};

const roleBadgeCls = 'inline-block text-[10px] font-semibold tracking-widest uppercase text-lawkitBlue-90 bg-lawkitBlue-10 rounded-full px-2 py-0.5 w-fit';

const hubLabelCls = [
  'absolute z-10 -translate-y-1/2 flex max-w-[190px] items-center gap-1.5 rounded-full bg-lawkitGray-950 px-3 py-1.5 text-[12px] font-semibold leading-snug whitespace-normal text-white',
  'hover:bg-lawkitGray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lawkitBlue-70 focus-visible:ring-offset-2',
].join(' ');

function chipCls(isActive: boolean, isWorld: boolean) {
  return (
    'absolute z-10 flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[13px] font-semibold text-lawkitGray-800' +
    ' shadow-sm cursor-pointer select-none whitespace-nowrap -translate-x-1/2 -translate-y-1/2' +
    ' motion-safe:transition-[border-color,box-shadow,background-color] motion-safe:duration-200' +
    ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lawkitBlue-70 focus-visible:ring-offset-2' +
    (isWorld ? ' border border-dashed' : ' border') +
    (isActive ? ' border-lawkitBlue-70 bg-lawkitBlue-10 shadow-md' : ' border-lawkitGray-200 hover:border-lawkitBlue-70 hover:shadow-md')
  );
}

/** Advisor list for one region — shared by the desktop popover and mobile panel. */
function RegionDetail({ region }: { region: GlobalRegion }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        {region.flag && <CountryFlag code={region.flag} className="w-[22px] shrink-0" />}
        <p className="text-[14px] font-bold text-lawkitGray-950">{region.name}</p>
      </div>
      {region.note && <p className="text-[12px] leading-relaxed text-lawkitGray-500 mt-2">{region.note}</p>}
      <ul className="mt-3 divide-y divide-lawkitGray-100">
        {region.advisors.map(advisor => (
          <li key={advisor.name} className="py-2.5 first:pt-0 last:pb-0">
            <span className={roleBadgeCls}>{advisor.role}</span>
            <p className="text-[13px] font-semibold text-lawkitGray-900 mt-1">{advisor.name}</p>
            <p className="text-[12px] text-lawkitGray-500 mt-0.5">{advisor.note}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface WorldMapSvgProps {
  regions: GlobalRegion[];
  activeRegionId: string | null;
  regionHrefPrefix?: string;
  hubHref?: string;
  onShow: (id: string) => void;
  onHide: () => void;
  onTogglePin: (id: string) => void;
  copy: GlobalNetworkCopy;
  className?: string;
}

/** World map layer shared by desktop and mobile layouts. */
function WorldMapSvg({ regions, activeRegionId, regionHrefPrefix, hubHref, onShow, onHide, onTogglePin, copy, className = 'w-full h-auto' }: WorldMapSvgProps) {
  const laidOutRegions = regions.filter(region => NODE_LAYOUT[region.id]);
  const regionById = new Map(regions.map(region => [region.id, region]));
  const hubMarker = (
    <>
      <circle className="gnet-ping" cx={HUB.x} cy={HUB.y} r="7" fill="none" stroke="#346EDA" strokeWidth="1.5" />
      <circle cx={HUB.x} cy={HUB.y} r="7" fill="#346EDA" />
      <circle cx={HUB.x} cy={HUB.y} r="3" fill="#FFFFFF" />
    </>
  );

  return (
    <ComposableMap width={VIEW_W} height={VIEW_H} projection={MAP_PROJECTION} projectionConfig={MAP_PROJECTION_CONFIG} className={className} aria-label={copy.hint}>
      <rect x="8" y="8" width={VIEW_W - 16} height={VIEW_H - 16} rx="24" fill="#F8FBFF" stroke="#DEE9FB" />

      {/* Natural Earth country boundaries — bundled locally, so no map API is requested. */}
      <Geographies geography={worldMapData}>
        {({ geographies }) =>
          geographies.map(geography => {
            const targetId = geography.id == null ? undefined : COUNTRY_REGION_BY_NUMERIC_ID[String(geography.id)];
            const region = targetId ? regionById.get(targetId) : undefined;
            const isActive = region ? activeRegionId === region.id : false;
            const regionHref = regionHrefPrefix && region ? `${regionHrefPrefix}${region.id}` : undefined;
            const regionLabel = region ? copy.regionAria.replace('{region}', region.name) : undefined;
            const geographyStyle = {
              default: {
                fill: region ? (isActive ? '#7FAAF0' : '#C9DCF8') : '#E8F0FA',
                stroke: region ? (isActive ? '#2F63C5' : '#7AA8EC') : '#C7D8ED',
                strokeWidth: region ? 1 : 0.6,
                outline: 'none',
              },
              hover: {
                fill: region ? '#5A91E5' : '#DCE8F7',
                stroke: region ? '#2458B8' : '#B4CBE6',
                strokeWidth: region ? 1.2 : 0.8,
                outline: 'none',
              },
              pressed: {
                fill: region ? '#346EDA' : '#DCE8F7',
                stroke: region ? '#1D4CA3' : '#B4CBE6',
                strokeWidth: region ? 1.2 : 0.8,
                outline: 'none',
              },
            };
            const geographyShape = (
              <Geography
                geography={geography}
                className={region ? 'gnet-country' : 'gnet-geography'}
                style={geographyStyle}
                tabIndex={-1}
                onMouseEnter={region ? () => onShow(region.id) : undefined}
                onMouseLeave={region ? onHide : undefined}
              />
            );

            if (regionHref && region && regionLabel) {
              return (
                <a
                  key={geography.rsmKey ?? String(geography.id)}
                  href={regionHref}
                  className="gnet-country-anchor"
                  aria-label={regionLabel}
                  onMouseEnter={() => onShow(region.id)}
                  onMouseLeave={onHide}
                  onFocus={() => onShow(region.id)}
                  onBlur={onHide}
                  onClick={() => onTogglePin(region.id)}
                >
                  {geographyShape}
                </a>
              );
            }

            return (
              <g
                key={geography.rsmKey ?? String(geography.id)}
                aria-hidden={region ? undefined : true}
                onMouseEnter={region ? () => onShow(region.id) : undefined}
                onMouseLeave={region ? onHide : undefined}
              >
                {geographyShape}
              </g>
            );
          })
        }
      </Geographies>

      {/* Seoul hub → country arcs. */}
      {laidOutRegions.map((region, index) => {
        const layout = NODE_LAYOUT[region.id];
        const path = arcPath(layout);
        const isActive = activeRegionId === region.id;
        return (
          <g key={`arc-${region.id}`}>
            <path
              className="gnet-arc"
              d={path}
              pathLength={1}
              fill="none"
              stroke={isActive ? '#346EDA' : '#9DBEF1'}
              strokeWidth={isActive ? 2 : 1.5}
              style={{ transitionDelay: `${0.15 + index * 0.12}s` }}
            />
            <circle className="gnet-dot" r="3.5" fill="#5A91E5" style={{ offsetPath: `path('${path}')`, animationDelay: `${index * 0.9}s` }} />
            <circle cx={layout.x} cy={layout.y} r="4" fill={isActive ? '#346EDA' : '#9DBEF1'} />
          </g>
        );
      })}

      {/* Seoul hub marker. */}
      {hubHref ? (
        <a href={hubHref} className="gnet-hub-anchor" aria-label={copy.hub}>
          {hubMarker}
        </a>
      ) : (
        hubMarker
      )}
    </ComposableMap>
  );
}

interface GlobalNetworkProps {
  regions: GlobalRegion[];
  copy?: GlobalNetworkCopy;
  /** Optional same-page href prefix for country controls (e.g. '#global-region-'). */
  regionHrefPrefix?: string;
  /** Optional same-page href for the Seoul hub (e.g. '#korea-team'). */
  hubHref?: string;
}

export default function GlobalNetwork({ regions, copy = KO_COPY, regionHrefPrefix, hubHref }: GlobalNetworkProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerInPopoverRef = useRef(false);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);

  // The map communicates the named country hubs. The non-country coverage row
  // remains available in the roster below, but does not compete with those
  // hubs as a map destination.
  const mapRegions = regions.filter(region => region.id !== 'world');
  const active = hovered ?? pinned;
  const activeRegion = mapRegions.find(region => region.id === active) ?? null;
  const panelRegion = mapRegions.find(region => region.id === (pinned ?? active)) ?? mapRegions[0];

  // Draw-in once the map scrolls into view (same pattern as Reveal).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setInView(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Escape or pointer-down outside closes the popover — touch devices get no
  // hover/blur path, so the outside tap is their only discoverable dismissal.
  useEffect(() => {
    const close = () => {
      setHovered(null);
      setPinned(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const onPointerDown = (event: PointerEvent) => {
      const el = containerRef.current;
      if (el && event.target instanceof Node && !el.contains(event.target)) close();
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const cancelHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const show = (id: string) => {
    cancelHide();
    setHovered(id);
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => {
      // A click inside the popover blurs the chip and re-arms this timer while
      // the pointer never re-enters — keep the popover open in that case.
      if (!pointerInPopoverRef.current) setHovered(null);
    }, HIDE_DELAY_MS);
  };

  const togglePin = (id: string) => setPinned(current => (current === id ? null : id));

  const laidOutRegions = mapRegions.filter(region => NODE_LAYOUT[region.id]);
  const activeLayout = activeRegion ? NODE_LAYOUT[activeRegion.id] : null;

  return (
    <div ref={containerRef} className={inView ? 'gnet-in' : ''}>
      <style>{`
        /* Keep this block free of quotes and angle brackets: React escapes them
           in style-tag text children, which desyncs SSR HTML and breaks hydration. */
        .gnet-arc { stroke-dasharray: 1; stroke-dashoffset: 0; }
        .gnet-dot, .gnet-ping { opacity: 0; }
        .gnet-country-anchor { cursor: pointer; }
        .gnet-hub-anchor { cursor: pointer; }
        .gnet-hub-anchor:focus-visible { outline: 2px solid #346EDA; outline-offset: 3px; }
        .gnet-country, .gnet-geography { transition: fill 0.2s ease, stroke 0.2s ease; }
        @media (prefers-reduced-motion: no-preference) {
          .gnet-arc { stroke-dashoffset: 1; transition: stroke-dashoffset 1.1s ease-out; }
          .gnet-in .gnet-arc { stroke-dashoffset: 0; }
          @supports (offset-distance: 0%) {
            .gnet-in .gnet-dot { animation: gnet-travel 5.5s ease-in-out infinite; }
          }
          .gnet-in .gnet-ping { animation: gnet-ping 2.6s ease-out infinite; transform-origin: ${HUB.x}px ${HUB.y}px; }
          .gnet-pop { animation: gnet-pop 0.22s ease-out both; }
        }
        @keyframes gnet-travel {
          0% { offset-distance: 0%; opacity: 0; }
          12% { opacity: 1; }
          82% { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }
        @keyframes gnet-ping {
          0% { transform: scale(1); opacity: 0.7; }
          75%, 100% { transform: scale(3.2); opacity: 0; }
        }
        @keyframes gnet-pop {
          from { opacity: 0; transform: var(--gnet-pop-from); }
          to { opacity: 1; transform: var(--gnet-pop-to); }
        }
      `}</style>

      {/* ===== Desktop / tablet: world map ===== */}
      <div className="hidden sm:block max-w-[900px] mx-auto">
        {/* Percentage-anchored overlays must resolve against the SVG box only,
            so the caption below stays outside this relative container. */}
        <div className="relative">
          <WorldMapSvg regions={mapRegions} activeRegionId={active} regionHrefPrefix={regionHrefPrefix} hubHref={hubHref} onShow={show} onHide={scheduleHide} onTogglePin={togglePin} copy={copy} />

          {/* Hub label */}
          {hubHref ? (
            <a href={hubHref} className={hubLabelCls} style={{ left: pct(HUB.x + 60, VIEW_W), top: pct(HUB.y, VIEW_H) }} aria-label={copy.hub}>
              <CountryFlag code="KR" className="w-[18px] shrink-0" />
              {copy.hub}
            </a>
          ) : (
            <div
              className="absolute z-10 -translate-y-1/2 flex items-center gap-1.5 rounded-full bg-lawkitGray-950 text-white px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap pointer-events-none"
              style={{ left: pct(HUB.x + 60, VIEW_W), top: pct(HUB.y, VIEW_H) }}
            >
              <CountryFlag code="KR" className="w-[18px] shrink-0" />
              {copy.hub}
            </div>
          )}

          {/* Region chips */}
          {laidOutRegions.map(region => {
            const layout = NODE_LAYOUT[region.id];
            const regionHref = regionHrefPrefix ? `${regionHrefPrefix}${region.id}` : undefined;
            const chipContent = (
              <>
                {region.flag && <CountryFlag code={region.flag} className="w-[18px] shrink-0" />}
                {region.name}
              </>
            );

            if (regionHref) {
              return (
                <a
                  key={region.id}
                  href={regionHref}
                  className={chipCls(active === region.id, region.id === 'world')}
                  style={chipPosition(layout)}
                  onMouseEnter={() => show(region.id)}
                  onMouseLeave={scheduleHide}
                  onFocus={() => show(region.id)}
                  onBlur={scheduleHide}
                  onClick={() => togglePin(region.id)}
                  aria-expanded={active === region.id}
                  aria-controls="gnet-popover"
                  aria-label={copy.regionAria.replace('{region}', region.name)}
                >
                  {chipContent}
                </a>
              );
            }

            return (
              <button
                key={region.id}
                type="button"
                className={chipCls(active === region.id, region.id === 'world')}
                style={chipPosition(layout)}
                onMouseEnter={() => show(region.id)}
                onMouseLeave={scheduleHide}
                onFocus={() => show(region.id)}
                onBlur={scheduleHide}
                onClick={() => togglePin(region.id)}
                aria-expanded={active === region.id}
                aria-controls="gnet-popover"
                aria-label={copy.regionAria.replace('{region}', region.name)}
              >
                {chipContent}
              </button>
            );
          })}

          {/* Popover */}
          {activeRegion && activeLayout && (
            <div
              key={activeRegion.id}
              id="gnet-popover"
              className="gnet-pop absolute z-20 w-[290px] bg-white rounded-2xl border border-lawkitGray-200 shadow-xl p-4"
              style={
                {
                  left: pct(activeLayout.x, VIEW_W),
                  top: pct(activeLayout.y - 30, VIEW_H),
                  transform: POPOVER_TRANSFORM[activeLayout.side],
                  '--gnet-pop-from': `${POPOVER_TRANSFORM[activeLayout.side]} translateY(6px)`,
                  '--gnet-pop-to': POPOVER_TRANSFORM[activeLayout.side],
                } as React.CSSProperties
              }
              onMouseEnter={() => {
                pointerInPopoverRef.current = true;
                cancelHide();
              }}
              onMouseLeave={() => {
                pointerInPopoverRef.current = false;
                scheduleHide();
              }}
            >
              <RegionDetail region={activeRegion} />
            </div>
          )}
        </div>

        <p className="text-[12px] text-lawkitGray-400 text-center mt-2">{copy.hint}</p>

        {/* Screen-reader equivalent of the hover-only popover content. */}
        <div className="sr-only">
          <h4>{copy.srHeading}</h4>
          <ul>
            {mapRegions.map(region => (
              <li key={region.id}>
                {region.name}: {region.advisors.map(advisor => `${advisor.name} (${advisor.role}, ${advisor.note})`).join(', ')}
                {region.note ? ` — ${region.note}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ===== Mobile: region chips + detail panel ===== */}
      <div className="sm:hidden">
        <div className="mb-5 overflow-hidden rounded-2xl border border-lawkitGray-200 bg-white">
          <WorldMapSvg regions={mapRegions} activeRegionId={active} regionHrefPrefix={regionHrefPrefix} hubHref={hubHref} onShow={show} onHide={scheduleHide} onTogglePin={togglePin} copy={copy} />
          <p className="flex items-center justify-center gap-1.5 border-t border-lawkitGray-100 px-4 py-3 text-center text-[11px] font-semibold leading-relaxed text-lawkitGray-700">
            <CountryFlag code="KR" className="w-[18px] shrink-0" />
            {copy.hub}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {mapRegions.map(region => {
            const isSelected = panelRegion.id === region.id;
            const regionHref = regionHrefPrefix ? `${regionHrefPrefix}${region.id}` : undefined;
            const chipContent = (
              <>
                {region.flag && <CountryFlag code={region.flag} className="w-[18px] shrink-0" />}
                {region.name}
              </>
            );

            if (regionHref) {
              return (
                <a
                  key={region.id}
                  href={regionHref}
                  className={
                    'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap' +
                    ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lawkitBlue-70 focus-visible:ring-offset-2' +
                    (region.id === 'world' ? ' border border-dashed' : ' border') +
                    (isSelected ? ' border-lawkitBlue-70 bg-lawkitBlue-10 text-lawkitBlue-90' : ' border-lawkitGray-200 bg-white text-lawkitGray-700')
                  }
                  onClick={() => setPinned(region.id)}
                  aria-label={copy.regionAria.replace('{region}', region.name)}
                >
                  {chipContent}
                </a>
              );
            }

            return (
              <button
                key={region.id}
                type="button"
                aria-pressed={isSelected}
                className={
                  'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap' +
                  ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lawkitBlue-70 focus-visible:ring-offset-2' +
                  (region.id === 'world' ? ' border border-dashed' : ' border') +
                  (isSelected ? ' border-lawkitBlue-70 bg-lawkitBlue-10 text-lawkitBlue-90' : ' border-lawkitGray-200 bg-white text-lawkitGray-700')
                }
                onClick={() => setPinned(region.id)}
              >
                {chipContent}
              </button>
            );
          })}
        </div>
        <div className="mt-4 bg-white rounded-2xl border border-lawkitGray-200 p-4">
          <RegionDetail region={panelRegion} />
        </div>
      </div>
    </div>
  );
}
